import { useEffect, useState, useCallback } from 'react';
import { getSubscription, cancelSubscription, createSubscription } from '@genfun/core/api/auth';
import { openTabWithUrl } from '@genfun/core/utils/openTabWithUrl';
import './AccountPopup.css';

const TIER_META = {
    1: {
        name: 'Starter',
        price: '$10',
        period: '/w',
        features: ['50 credits', '≈ 16 photos or 5 videos', 'No watermark'],
    },
    2: {
        name: 'Premium',
        price: '$20',
        period: '/w',
        features: ['200 credits', '≈ 66 photos or 20 videos', 'Priority queue'],
    },
};

const PLAN_CARDS = [
    {
        tier: 1,
        name: 'Starter',
        price: '$10',
        period: '/w',
        features: ['50 credits', '≈ 16 photos or 5 videos', 'No watermark', 'Cancel anytime'],
    },
    {
        tier: 2,
        name: 'Premium',
        price: '$20',
        strike: '$60',
        period: '/w',
        popular: true,
        features: ['200 credits', '≈ 66 photos or 20 videos', 'Priority queue', 'Cancel anytime'],
    },
];

const BUY_BTN_STYLE = {
    background: '#ff9000',
    color: '#000',
    border: 'none',
    fontWeight: 800,
    boxShadow: '0 6px 18px rgba(255,144,0,0.3)',
};

const STATUS_META = {
    active:    { label: 'Active',    tone: 'good' },
    cancelled: { label: 'Cancelled', tone: 'warn' },
    past_due:  { label: 'Past due',  tone: 'bad'  },
};

function formatDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Centrobill subscriptions in our setup are weekly (SUB_CYCLE_DAYS=7 on the backend). We don't get an explicit
// next-billing date from the backend, so we derive it as last_rebill_at + 7 days
// (or started_at + 7 days if there hasn't been a rebill yet). Marked as
// estimated in the UI.
function computeNextBilling(sub) {
    if (!sub) return null;
    const anchor = sub.last_rebill_at || sub.started_at;
    if (!anchor) return null;
    const d = new Date(anchor);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + 7);
    return d;
}

function daysUntil(date) {
    if (!date) return null;
    const ms = date.getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function AccountPopup({ isOpen, onClose, token, user, refreshUser }) {
    const [sub, setSub] = useState(null);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [subscribingTier, setSubscribingTier] = useState(null);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!token) { setSub(null); setStatus(null); return; }
        setLoading(true);
        setError(null);
        try {
            const [data, code] = await getSubscription(token);
            setSub(data);
            setStatus(code);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!isOpen) return;
        document.body.style.overflow = 'hidden';
        refresh();
        return () => { document.body.style.overflow = ''; };
    }, [isOpen, refresh]);

    const handleSubscribe = (tier) => {
        if (!token || subscribingTier !== null) return;
        setSubscribingTier(tier);
        setError(null);
        openTabWithUrl(() => createSubscription(token, tier).then(r => r.url))
            .catch(e => setError(e.message || String(e)))
            .finally(() => setSubscribingTier(null));
    };

    const handleCancel = async () => {
        if (!confirmCancel) { setConfirmCancel(true); return; }
        setCancelling(true);
        setError(null);
        try {
            const [data, code] = await cancelSubscription(token);
            if (code !== 200) {
                setError((data && data.detail) || 'Failed to cancel');
                return;
            }
            setConfirmCancel(false);
            await refresh();
            refreshUser?.();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setCancelling(false);
        }
    };

    if (!isOpen) return null;

    const hasSub      = status === 200 && sub;
    const isActive    = hasSub && sub.status === 'active';
    const tier        = hasSub ? TIER_META[sub.tier] || { name: `Tier ${sub.tier}`, price: '', period: '', features: [] } : null;
    const statusMeta  = hasSub ? (STATUS_META[sub.status] || { label: sub.status, tone: 'warn' }) : null;
    const isCancelled = hasSub && sub.status === 'cancelled';
    const nextBilling = hasSub && !isCancelled ? computeNextBilling(sub) : null;
    const accessUntil = hasSub && isCancelled ? computeNextBilling(sub) : null;
    const credits     = user?.credits ?? 0;

    return (
        <div className="mnAccountOverlay" onClick={onClose}>
            <div className="mnAccountPopup" onClick={e => e.stopPropagation()}>
                <button className="mnAccountClose" onClick={onClose} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                <div className="mnAccountHeader">
                    <div className="mnAccountAvatar">
                        {(user?.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="mnAccountIdentity">
                        <div className="mnAccountName">My Account</div>
                        <div className="mnAccountEmail">{user?.email || '—'}</div>
                    </div>
                </div>

                {hasSub && (
                    <div className="mnAccountCreditsCard">
                        <div className="mnAccountCreditsLabel">Credits available</div>
                        <div className="mnAccountCreditsValue">
                            <span className="mnAccountCreditsNum">{credits}</span>
                            <span className="mnAccountCreditsUnit">credits</span>
                        </div>
                        {tier && (
                            <div className="mnAccountCreditsHint">
                                {isActive
                                    ? `Refills with your ${tier.name} plan`
                                    : accessUntil
                                        ? `Use them by ${formatDate(accessUntil.toISOString())}`
                                        : 'Use them before your access ends'}
                            </div>
                        )}
                    </div>
                )}

                <div className="mnAccountSectionTitle">Subscription</div>

                {loading && !sub && (
                    <div className="mnAccountLoading">Loading…</div>
                )}

                {!loading && status === 404 && (
                    <div className="mnAccountPlans">
                        <div className="mnAccountPlansIntro">
                            Pick a plan — cancel anytime.
                        </div>
                        <div className="mnAccountPlanCards">
                            {PLAN_CARDS.map(p => (
                                <div
                                    key={p.tier}
                                    className={`mnAccountPlan${p.popular ? ' mnAccountPlan--popular' : ''}`}
                                >
                                    {p.popular && <div className="mnAccountPlanBadge">Best value</div>}
                                    <div className="mnAccountPlanName">{p.name}</div>
                                    <div className="mnAccountPlanPrice">
                                        {p.price}
                                        <span className="mnAccountPlanPer">{p.period}</span>
                                        {p.strike && <s>{p.strike}</s>}
                                    </div>
                                    <ul className="mnAccountPlanFeatures">
                                        {p.features.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                    <button
                                        className={`mnAccountPlanBtn${p.popular ? ' mnAccountPlanBtn--primary' : ''}`}
                                        style={BUY_BTN_STYLE}
                                        onClick={() => handleSubscribe(p.tier)}
                                        disabled={subscribingTier !== null}
                                    >
                                        {subscribingTier === p.tier ? 'Redirecting…' : 'Buy'}
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="mnAccountPlansFoot">
                            Secure checkout via Centrobill.
                        </div>
                    </div>
                )}

                {!loading && hasSub && (
                    <div className="mnAccountSubCard">
                        <div className="mnAccountSubTop">
                            <div>
                                <div className="mnAccountSubPlan">{tier.name}</div>
                                <div className="mnAccountSubPrice">
                                    {tier.price}<span className="mnAccountSubPeriod">{tier.period}</span>
                                </div>
                            </div>
                            <span className={`mnAccountStatus mnAccountStatus--${statusMeta.tone}`}>
                                <span className="mnAccountStatusDot" /> {statusMeta.label}
                            </span>
                        </div>

                        <div className="mnAccountSubRows">
                            {sub.started_at && (
                                <div className="mnAccountSubRow">
                                    <span className="mnAccountSubRowLabel">Started</span>
                                    <span className="mnAccountSubRowVal">{formatDate(sub.started_at)}</span>
                                </div>
                            )}
                            {sub.last_rebill_at && (
                                <div className="mnAccountSubRow">
                                    <span className="mnAccountSubRowLabel">Last payment</span>
                                    <span className="mnAccountSubRowVal">{formatDate(sub.last_rebill_at)}</span>
                                </div>
                            )}
                            {nextBilling && (
                                <div className="mnAccountSubRow mnAccountSubRow--accent">
                                    <span className="mnAccountSubRowLabel">Next renewal</span>
                                    <span className="mnAccountSubRowVal">
                                        {formatDate(nextBilling.toISOString())}
                                        {daysUntil(nextBilling) !== null && daysUntil(nextBilling) >= 0 && (
                                            <span className="mnAccountSubRowMeta"> · in {daysUntil(nextBilling)} day{daysUntil(nextBilling) === 1 ? '' : 's'}</span>
                                        )}
                                    </span>
                                </div>
                            )}
                            {isCancelled && accessUntil && (
                                <div className="mnAccountSubRow mnAccountSubRow--accent">
                                    <span className="mnAccountSubRowLabel">Access until</span>
                                    <span className="mnAccountSubRowVal">{formatDate(accessUntil.toISOString())}</span>
                                </div>
                            )}
                            {sub.cancelled_at && (
                                <div className="mnAccountSubRow">
                                    <span className="mnAccountSubRowLabel">Cancelled on</span>
                                    <span className="mnAccountSubRowVal">{formatDate(sub.cancelled_at)}</span>
                                </div>
                            )}
                        </div>

                        {tier.features?.length > 0 && (
                            <ul className="mnAccountFeatures">
                                {tier.features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                        )}

                        {isCancelled ? (
                            <div className="mnAccountRenew">
                                <button
                                    className="mnAccountRenewBtn"
                                    onClick={() => handleSubscribe(sub.tier)}
                                    disabled={subscribingTier !== null}
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 4 21 10 15 10"/></svg>
                                    {subscribingTier === sub.tier
                                        ? 'Redirecting…'
                                        : `Renew ${tier.name} — ${tier.price}${tier.period}`}
                                </button>
                                <div className="mnAccountRenewNote">
                                    You keep access until {accessUntil ? formatDate(accessUntil.toISOString()) : 'the period ends'}.
                                </div>
                            </div>
                        ) : (
                            <div className="mnAccountActions">
                                <button
                                    className={`mnAccountCancel${confirmCancel ? ' mnAccountCancel--confirm' : ''}`}
                                    onClick={handleCancel}
                                    disabled={cancelling}
                                >
                                    {cancelling
                                        ? 'Cancelling…'
                                        : confirmCancel
                                            ? 'Click again to confirm cancel'
                                            : 'Cancel subscription'}
                                </button>
                                {confirmCancel && !cancelling && (
                                    <button className="mnAccountCancelBack" onClick={() => setConfirmCancel(false)}>
                                        Keep subscription
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {!loading && hasSub && isCancelled && (
                    <div className="mnAccountPlans mnAccountPlans--inline">
                        <div className="mnAccountPlansDivider">
                            <span>or switch plan</span>
                        </div>
                        <div className="mnAccountPlanCards">
                            {PLAN_CARDS.filter(p => p.tier !== sub.tier).map(p => (
                                <div
                                    key={p.tier}
                                    className={`mnAccountPlan${p.popular ? ' mnAccountPlan--popular' : ''}`}
                                >
                                    {p.popular && <div className="mnAccountPlanBadge">Best value</div>}
                                    <div className="mnAccountPlanName">{p.name}</div>
                                    <div className="mnAccountPlanPrice">
                                        {p.price}
                                        <span className="mnAccountPlanPer">{p.period}</span>
                                        {p.strike && <s>{p.strike}</s>}
                                    </div>
                                    <ul className="mnAccountPlanFeatures">
                                        {p.features.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                    <button
                                        className={`mnAccountPlanBtn${p.popular ? ' mnAccountPlanBtn--primary' : ''}`}
                                        style={BUY_BTN_STYLE}
                                        onClick={() => handleSubscribe(p.tier)}
                                        disabled={subscribingTier !== null}
                                    >
                                        {subscribingTier === p.tier ? 'Redirecting…' : `Switch to ${p.name}`}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {error && <div className="mnAccountError">{error}</div>}
            </div>
        </div>
    );
}
